import { useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRecipeStore } from '../../stores/recipeStore';
import type { PdfImportResult } from '../../types/recipe';
import './recipes.css';

type Phase = 'idle' | 'uploading' | 'results';

const MAX_FILES = 10;

export function PdfImport() {
  const navigate = useNavigate();
  const { importFromPdf } = useRecipeStore();

  const [phase, setPhase] = useState<Phase>('idle');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<PdfImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndSetFiles = useCallback((files: File[]) => {
    setValidationError(null);

    const pdfs = files.filter((f) => f.type === 'application/pdf');
    if (pdfs.length !== files.length) {
      setValidationError('Only PDF files are accepted.');
      return;
    }

    const merged = [...selectedFiles, ...pdfs];
    if (merged.length > MAX_FILES) {
      setValidationError(`Maximum ${MAX_FILES} files allowed.`);
      return;
    }

    setSelectedFiles(merged);
  }, [selectedFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      validateAndSetFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    validateAndSetFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImport = async () => {
    if (selectedFiles.length === 0) return;
    setPhase('uploading');
    setImportError(null);
    try {
      const res = await importFromPdf(selectedFiles);
      setResult(res);
      setPhase('results');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
      setPhase('idle');
    }
  };

  const handleReset = () => {
    setPhase('idle');
    setSelectedFiles([]);
    setResult(null);
    setValidationError(null);
    setImportError(null);
  };

  if (phase === 'uploading') {
    return (
      <div className="recipe-container">
        <div className="loading-state" role="status" aria-live="polite">
          <p>Processing with AI… This may take a minute per file.</p>
        </div>
      </div>
    );
  }

  if (phase === 'results' && result) {
    return (
      <div className="recipe-container">
        <div className="recipe-header">
          <h1>Import Results</h1>
          <p className="recipe-subtitle">{result.status.message}</p>
        </div>

        {result.recipes.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h2>Imported Recipes</h2>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {result.recipes.map((recipe) => (
                <li key={recipe.id} style={{ marginBottom: '0.5rem' }}>
                  <Link to={`/recipes/${recipe.id}`} className="btn-secondary">
                    {recipe.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.failed.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h2>Failed Imports</h2>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {result.failed.map((failure, i) => (
                <li key={i} style={{ marginBottom: '0.5rem' }}>
                  <strong>{failure.filename}</strong>: {failure.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={handleReset}>
            Import More
          </button>
          <button className="btn-primary" onClick={() => navigate('/recipes')}>
            View All Recipes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="recipe-container">
      <div className="recipe-header">
        <h1>Import Recipes from PDF</h1>
        <p className="recipe-subtitle">
          Upload up to {MAX_FILES} PDF files. Text will be extracted and parsed by AI.
        </p>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: '2px dashed var(--color-border, #ccc)',
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          cursor: 'pointer',
          marginBottom: '1rem',
        }}
        role="button"
        tabIndex={0}
        aria-label="Drop PDF files here or click to select"
        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
      >
        <p>Drop PDF files here or click to select</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
          aria-hidden="true"
        />
      </div>

      {validationError && (
        <div className="error-message" role="alert" style={{ marginBottom: '1rem' }}>
          {validationError}
        </div>
      )}

      {importError && (
        <div className="error-message" role="alert" style={{ marginBottom: '1rem' }}>
          {importError}
        </div>
      )}

      {selectedFiles.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1rem' }}>
          {selectedFiles.map((file, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span>{file.name}</span>
              <button
                onClick={() => removeFile(i)}
                aria-label={`Remove ${file.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger, red)' }}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <button
          className="btn-primary"
          onClick={handleImport}
          disabled={selectedFiles.length === 0}
        >
          Import {selectedFiles.length > 0 ? `(${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''})` : ''}
        </button>
        <Link to="/recipes" className="btn-secondary">
          Cancel
        </Link>
      </div>
    </div>
  );
}
